import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { TagInput } from '@/components/ui/tag-input';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useCompanyMembers } from '@/hooks/useCompanyMembers';
import { createTask, getTaskPriorityOptions, getTaskCategoryOptions } from '@/services/leads/taskService';
import { logActivity } from '@/services/leads/activityService';
import { useToast } from '@/hooks/use-toast';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface AddTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddTaskDialog: React.FC<AddTaskDialogProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: companyMembers, isLoading: isLoadingMembers } = useCompanyMembers();
  
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [priority, setPriority] = useState('medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const priorityOptions = getTaskPriorityOptions();
  const categoryOptions = getTaskCategoryOptions();

  // Fetch leads for dropdown - filtered by user
  const { data: leads, isLoading: isLoadingLeads } = useQuery({
    queryKey: ['leads', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        console.warn('No user ID available');
        return [];
      }
      
      // Fetch only leads created by the current user
      const { data, error } = await supabase
        .from('leads')
        .select('id, name')
        .eq('created_by', user.id)
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isOpen,
  });

  const resetForm = () => {
    setSelectedLeadId('');
    setTitle('');
    setDescription('');
    setDueDate(undefined);
    setPriority('medium');
    setAssignedTo('');
    setCategories([]);
    setIsSubmitting(false);
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedLeadId) {
      toast({
        title: 'Lead Required',
        description: 'Please select a lead for this task',
        variant: 'destructive',
      });
      return;
    }

    if (!title || title.trim().length < 3) {
      toast({
        title: 'Title Required',
        description: 'Task title must be at least 3 characters',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the task
      const newTask = await createTask({
        leadId: selectedLeadId,
        title: title.trim(),
        description: description.trim(),
        dueDate: dueDate?.toISOString() || null,
        priority: priority as 'low' | 'medium' | 'high',
        status: 'pending',
        assignedTo: assignedTo || user?.id || '',
        categories: categories,
        reminderTimes: [],
      });

      // Log activity
      await logActivity({
        leadId: selectedLeadId,
        type: 'task',
        title: 'Task Created',
        description: `Created task: ${title}`,
        metadata: { taskId: newTask.id },
        performedBy: user?.id || '',
      });

      // Invalidate queries to refresh task list
      queryClient.invalidateQueries({ queryKey: ['tasks'] });

      toast({
        title: 'Task Created',
        description: `"${title}" has been added successfully`,
      });

      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create task',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const assignedMember = companyMembers?.find(m => m.id === assignedTo);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Add New Task</DialogTitle>
        </DialogHeader>
        
        <DialogBody className="space-y-4">
          {/* Lead Selection */}
          <div className="space-y-2">
            <Label htmlFor="lead" className="required">Lead</Label>
            <Select value={selectedLeadId} onValueChange={setSelectedLeadId} disabled={isLoadingLeads}>
              <SelectTrigger id="lead">
                <SelectValue placeholder={isLoadingLeads ? "Loading leads..." : "Select a lead"} />
              </SelectTrigger>
              <SelectContent>
                {leads && leads.length > 0 ? (
                  leads.map((lead) => (
                    <SelectItem key={lead.id} value={lead.id}>
                      {lead.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-2 text-sm text-muted-foreground">
                    No leads available. Create a lead first.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="required">Task Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter task title"
              maxLength={200}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Add task details..."
            />
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Due Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dueDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, "PPP") : "Select due date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label>Priority</Label>
            <RadioGroup value={priority} onValueChange={setPriority}>
              <div className="flex gap-4">
                {priorityOptions.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={option.value} id={`priority-${option.value}`} />
                    <Label htmlFor={`priority-${option.value}`} className="cursor-pointer font-normal">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Categories */}
          <div className="space-y-2">
            <Label>Categories</Label>
            <TagInput
              selected={categories}
              onChange={setCategories}
              placeholder="Add categories..."
              options={categoryOptions.map(c => ({ label: c.label, value: c.value }))}
              creatable={true}
            />
          </div>

          {/* Assigned To */}
          <div className="space-y-2">
            <Label htmlFor="assignedTo">Assign To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo} disabled={isLoadingMembers}>
              <SelectTrigger id="assignedTo">
                <SelectValue placeholder={isLoadingMembers ? "Loading members..." : "Select team member"}>
                  {assignedMember && (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={assignedMember.avatar_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {assignedMember.first_name?.[0]}{assignedMember.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span>{assignedMember.first_name} {assignedMember.last_name}</span>
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {companyMembers && companyMembers.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={member.avatar_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {member.first_name?.[0]}{member.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span>{member.first_name} {member.last_name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogBody>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedLeadId || !title.trim()}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Task'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddTaskDialog;
